import { ACCOUNT_FEATURE, noop, selectHasFeatureLoaded, selectUserRolePermissions } from '@socialive/common';
import { useFeature } from '@socialive/features';
import { Form, LoadingIcon, TextInputField } from '@socialive/ui';
import PropTypes from 'prop-types';
import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router';
import { Link } from 'react-router-dom';
import { selectAccountChannelsMap, selectChannelsLoaded, updateChannel } from '~/store';
import SpaceTabs from './SpaceTabs';

const FORM = {
  SPACE_NAME: 'spaceName',
};

const Space = ({ checkAuthorization }) => {
  const dispatch = useDispatch();
  const { spaceToken } = useParams();
  const history = useHistory();
  const { canManageChannels } = useSelector(selectUserRolePermissions);
  const [, { enabled: isSpacesEnabled }] = useFeature(ACCOUNT_FEATURE.SPACES);
  const isSplitLoaded = useSelector(selectHasFeatureLoaded(ACCOUNT_FEATURE.SPACES));
  const didChannelsLoad = useSelector(selectChannelsLoaded);
  const spacesMap = useSelector(selectAccountChannelsMap);
  const space = spacesMap[spaceToken];

  const form = useForm({ defaultValues: { [FORM.SPACE_NAME]: space?.name ?? '' } });
  const { watch, setValue } = form;

  const newSpaceName = watch('spaceName', space?.name ?? '');

  const goToChannelsList = useCallback(() => {
    history.push('/channels');
  }, [history]);

  const onSave = useCallback(async () => {
    try {
      await dispatch(updateChannel({ name: newSpaceName, id: space.id, trigger: 'Space@onSave' })).unwrap();
      goToChannelsList();
    } catch {
      setValue('spaceName', space.name);
    }
  }, [dispatch, space, newSpaceName, setValue, goToChannelsList]);

  useEffect(() => {
    const authorized = checkAuthorization('channels');
    if (!authorized) {
      return;
    }

    if ((isSplitLoaded && !isSpacesEnabled) || !canManageChannels) {
      history.push('/dashboard');
    }
  }, [isSpacesEnabled, canManageChannels, isSplitLoaded, checkAuthorization, history]);

  useEffect(() => {
    if (space?.name) {
      setValue('spaceName', space.name);
    }
  }, [space, setValue]);

  if (!didChannelsLoad || !isSplitLoaded) {
    return (
      <div className="flex justify-center gap-2">
        <LoadingIcon className="animate-spin-slow" /> Loading...
      </div>
    );
  }

  if (!space) {
    return (
      <div className="flex justify-center gap-2 flex-col">
        <p>Invalid channel</p>
        <Link to="/channels">Go back to the spaces list</Link>
      </div>
    );
  }

  return (
    <div className="content-container">
      <div className="pt-4 flex justify-between">
        <h2 className="">{space.name}</h2>
        <div>
          <button type="button" className="btn" onClick={goToChannelsList}>
            cancel
          </button>
          <button type="button" className="btn ml-4" onClick={onSave}>
            save
          </button>
        </div>
      </div>

      <Form form={form} onSubmit={noop} className="flex flex-col gap-2 pt-12">
        <TextInputField className="text-left" name="spaceName" label="Space Name" placeholder="name your space" />
      </Form>

      <div className="pt-12">
        <SpaceTabs />
      </div>
    </div>
  );
};

Space.propTypes = {
  checkAuthorization: PropTypes.func.isRequired,
};

export default Space;
